import { defineConfig } from "@twind/core";
import presetAutoprefix from "@twind/preset-autoprefix";
import presetTailwind from "@twind/preset-tailwind";

export default defineConfig({
  presets: [presetAutoprefix(), presetTailwind()],
  ignorelist: [
    /^recharts-/,
    /^legend-item-/,
    /^xAxis$/,
    /^yAxis$/,
    /^__/,
    /^jsx-/,
    /^line-clamp-/,
  ],
  theme: {
    extend: {
      fontFamily: {
        sans: [
          "Inter",
          "Roboto",
          "ui-sans-serif",
          "system-ui",
          "-apple-system",
          "Segoe UI",
          "Helvetica Neue",
          "Arial",
          "Noto Sans",
          "sans-serif",
          "Apple Color Emoji",
          "Segoe UI Emoji",
          "Segoe UI Symbol",
          "Noto Color Emoji",
        ],
      },
      colors: {
        // Paleta da marca por variavel CSS: o valor padrao (violeta Agiliza)
        // vive na raiz do documento, e a pagina publica do estabelecimento
        // sobrescreve as variaveis com a escala derivada da cor escolhida.
        // O placeholder de alfa mantem os usos com transparencia funcionando
        // (bg-primary-500/20 e afins) — conferido no navegador.
        primary: {
          DEFAULT: "rgb(var(--brand-600) / <alpha-value>)",
          hover: "rgb(var(--brand-hover) / <alpha-value>)",
          50: "rgb(var(--brand-50) / <alpha-value>)",
          100: "rgb(var(--brand-100) / <alpha-value>)",
          200: "rgb(var(--brand-200) / <alpha-value>)",
          300: "rgb(var(--brand-300) / <alpha-value>)",
          400: "rgb(var(--brand-400) / <alpha-value>)",
          500: "rgb(var(--brand-500) / <alpha-value>)",
          600: "rgb(var(--brand-600) / <alpha-value>)",
          700: "rgb(var(--brand-700) / <alpha-value>)",
          800: "rgb(var(--brand-800) / <alpha-value>)",
          900: "rgb(var(--brand-900) / <alpha-value>)",
          950: "rgb(var(--brand-950) / <alpha-value>)",
        },
        secondary: {
          DEFAULT: "#38bdf8",
          50: "#f0f9ff",
          100: "#e0f2fe",
          200: "#bae6fd",
          300: "#7dd3fc",
          400: "#38bdf8",
          500: "#0ea5e9",
          600: "#0284c7",
          700: "#0369a1",
          800: "#075985",
          900: "#0c4a6e",
          950: "#082f49",
        },
        slate: {
          950: "#020617",
        },
        accent: {
          DEFAULT: "#22c55e",
          50: "#f0fdf4",
          100: "#dcfce7",
          200: "#bbf7d0",
          300: "#86efac",
          400: "#4ade80",
          500: "#22c55e",
          600: "#16a34a",
          700: "#15803d",
          800: "#166534",
          900: "#14532d",
          950: "#052e16",
        },
      },
      spacing: {
        11: "2.75rem",
        12: "3rem",
        14: "3.5rem",
        18: "4.5rem",
        22: "5.5rem",
      },
      opacity: {
        45: "0.45",
      },
      borderWidth: {
        3: "3px",
      },
      borderRadius: {
        "4xl": "2rem",
      },
      boxShadow: {
        subtle: "0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.03)",
        soft: "0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05)",
        card: "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.05)",
        "card-hover":
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
        glow: "0 0 22px -6px rgba(124, 58, 237, 0.35)",
        "glow-lg": "0 0 46px -12px rgba(124, 58, 237, 0.45)",
      },
      animation: {
        fadeIn: "fadeIn 0.5s ease-out",
        "fade-in": "fadeIn 0.5s ease-out",
        "slide-up": "slideUp 0.4s ease-out",
        "scale-in": "scaleIn 0.3s ease-out",
        "spin-reverse": "spinReverse 1.5s linear infinite",
        // Folha subindo de baixo: rapida, com desaceleracao exponencial.
        "sheet-up": "sheetUp 0.22s cubic-bezier(0.16, 1, 0.3, 1) both",
        // Par da folha em telas grandes, onde o dialogo e centralizado.
        "dialog-in": "dialogIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) both",
        "backdrop-in": "backdropIn 0.18s ease-out both",
        // Painel lateral entrando pela direita.
        "sheet-right": "sheetRight 0.24s cubic-bezier(0.16, 1, 0.3, 1) both",
        // Pulso verde que aponta o botao "Abrir pagina" ao vir do "Meu link".
        "highlight-pulse": "highlightPulse 0.7s ease-in-out 8",
      },
      keyframes: {
        fadeIn: {
          "0%": { opacity: "0", transform: "translateY(10px)" },
          "100%": { opacity: "1", transform: "translateY(0)" },
        },
        slideUp: {
          "0%": { opacity: "0", transform: "translateY(20px)" },
          "100%": { opacity: "1", transform: "translateY(0)" },
        },
        scaleIn: {
          "0%": { opacity: "0", transform: "scale(0.95)" },
          "100%": { opacity: "1", transform: "scale(1)" },
        },
        spinReverse: {
          "0%": { transform: "rotate(360deg)" },
          "100%": { transform: "rotate(0deg)" },
        },
        sheetUp: {
          "0%": { transform: "translateY(100%)" },
          "100%": { transform: "translateY(0)" },
        },
        sheetRight: {
          "0%": { transform: "translateX(100%)" },
          "100%": { transform: "translateX(0)" },
        },
        dialogIn: {
          "0%": { opacity: "0", transform: "translateY(8px) scale(0.98)" },
          "100%": { opacity: "1", transform: "translateY(0) scale(1)" },
        },
        backdropIn: {
          "0%": { opacity: "0" },
          "100%": { opacity: "1" },
        },
        highlightPulse: {
          "0%, 100%": {
            transform: "scale(1)",
            boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.55)",
          },
          "50%": {
            transform: "scale(1.06)",
            boxShadow: "0 0 0 12px rgba(34, 197, 94, 0)",
          },
        },
      },
    },
  },
});
